/**
 * Role-Based Access Control for Frontend
 * Provides hooks and components for managing user permissions
 */

import { useAuth } from '../contexts/AuthContext';

// User roles enum
export enum UserRole {
  ADMIN = 'ADMIN',
  USER = 'USER',
  VIEWER = 'VIEWER',
}

// Permission types
export type Permission =
  | 'view_projects'
  | 'create_projects'
  | 'edit_projects'
  | 'delete_projects'
  | 'view_tasks'
  | 'create_tasks'
  | 'edit_tasks'
  | 'delete_tasks'
  | 'view_jobs'
  | 'create_jobs'
  | 'edit_jobs'
  | 'delete_jobs'
  | 'execute_jobs'
  | 'view_users'
  | 'create_users'
  | 'edit_users'
  | 'delete_users'
  | 'view_settings'
  | 'edit_settings';

// Role-Permission mapping
const rolePermissions: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: [
    // Full access to everything
    'view_projects', 'create_projects', 'edit_projects', 'delete_projects',
    'view_tasks', 'create_tasks', 'edit_tasks', 'delete_tasks',
    'view_jobs', 'create_jobs', 'edit_jobs', 'delete_jobs', 'execute_jobs',
    'view_users', 'create_users', 'edit_users', 'delete_users',
    'view_settings', 'edit_settings',
  ],
  [UserRole.USER]: [
    // Can manage own resources but not users
    'view_projects', 'create_projects', 'edit_projects', 'delete_projects',
    'view_tasks', 'create_tasks', 'edit_tasks', 'delete_tasks',
    'view_jobs', 'create_jobs', 'edit_jobs', 'delete_jobs', 'execute_jobs',
    'view_settings', // Can view but not edit global settings
  ],
  [UserRole.VIEWER]: [
    // Read-only access
    'view_projects',
    'view_tasks',
    'view_jobs',
    'view_settings',
  ],
};

/**
 * Hook to check if current user has a specific permission
 */
export function usePermission(permission: Permission): boolean {
  const { user } = useAuth();

  if (!user) return false;

  const userRole = user.role?.toUpperCase() as UserRole;
  const permissions = rolePermissions[userRole] || [];

  return permissions.includes(permission);
}

/**
 * Hook to check if current user has any of the specified roles
 */
export function useHasRole(...roles: UserRole[]): boolean {
  const { user } = useAuth();

  if (!user) return false;

  return roles.includes(user.role?.toUpperCase() as UserRole);
}

/**
 * Hook to get all permissions for current user
 */
export function usePermissions(): Permission[] {
  const { user } = useAuth();

  if (!user) return [];

  const userRole = user.role?.toUpperCase() as UserRole;
  return rolePermissions[userRole] || [];
}

/**
 * Hook to check multiple permissions at once
 */
export function useHasPermissions(...permissions: Permission[]): boolean {
  const userPermissions = usePermissions();
  return permissions.every(p => userPermissions.includes(p));
}

/**
 * Component to conditionally render content based on permissions
 */
interface ProtectedProps {
  children: React.ReactNode;
  permission?: Permission;
  permissions?: Permission[];
  role?: UserRole;
  roles?: UserRole[];
  requireAll?: boolean; // For permissions array: true = need all, false = need any
  fallback?: React.ReactNode;
}

export function Protected({
  children,
  permission,
  permissions,
  role,
  roles,
  requireAll = false,
  fallback = null
}: ProtectedProps): JSX.Element | null {
  const { user, isAuthenticated } = useAuth();

  // Not authenticated
  if (!isAuthenticated || !user) {
    return <>{fallback}</>;
  }

  // Normalize user role to uppercase for comparison
  const normalizedUserRole = user.role?.toUpperCase() as UserRole;

  // Check single role
  if (role && normalizedUserRole !== role) {
    return <>{fallback}</>;
  }

  // Check multiple roles
  if (roles && !roles.includes(normalizedUserRole)) {
    return <>{fallback}</>;
  }

  // Check single permission
  if (permission) {
    const userPermissions = rolePermissions[normalizedUserRole] || [];

    if (!userPermissions.includes(permission)) {
      return <>{fallback}</>;
    }
  }

  // Check multiple permissions
  if (permissions && permissions.length > 0) {
    const userPermissions = rolePermissions[normalizedUserRole] || [];

    const hasPermissions = requireAll
      ? permissions.every(p => userPermissions.includes(p))
      : permissions.some(p => userPermissions.includes(p));

    if (!hasPermissions) {
      return <>{fallback}</>;
    }
  }

  // User has required permissions
  return <>{children}</>;
}

/**
 * Higher-order component to protect routes
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredRoles?: UserRole[];
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requiredRoles,
  requiredPermission,
  requiredPermissions,
  redirectTo = '/login'
}: ProtectedRouteProps): JSX.Element {
  const { user, isAuthenticated } = useAuth();

  // Not authenticated - show login prompt
  if (!isAuthenticated || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-[#050505]">
        <div className="text-center p-8 bg-white dark:bg-[#111] rounded-lg shadow-lg max-w-md">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
            Authentication Required
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You need to be logged in to access this page.
          </p>
          <a
            href={redirectTo}
            className="inline-block px-6 py-3 bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 transition-colors"
          >
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  // Check role requirements
  const normalizedUserRole = user.role?.toUpperCase() as UserRole;

  if (requiredRole && normalizedUserRole !== requiredRole) {
    return <AccessDenied requiredRole={requiredRole} userRole={normalizedUserRole} />;
  }

  if (requiredRoles && !requiredRoles.includes(normalizedUserRole)) {
    return <AccessDenied requiredRoles={requiredRoles} userRole={normalizedUserRole} />;
  }

  // Check permission requirements
  if (requiredPermission || requiredPermissions) {
    const userPermissions = rolePermissions[normalizedUserRole] || [];

    if (requiredPermission && !userPermissions.includes(requiredPermission)) {
      return <AccessDenied requiredPermission={requiredPermission} userRole={normalizedUserRole} />;
    }

    if (requiredPermissions) {
      const hasAllPermissions = requiredPermissions.every(p => userPermissions.includes(p));
      if (!hasAllPermissions) {
        return <AccessDenied requiredPermissions={requiredPermissions} userRole={normalizedUserRole} />;
      }
    }
  }

  // User has required access
  return <>{children}</>;
}

/**
 * Access Denied component
 */
interface AccessDeniedProps {
  requiredRole?: UserRole;
  requiredRoles?: UserRole[];
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  userRole: UserRole;
}

function AccessDenied({
  requiredRole,
  requiredRoles,
  requiredPermission,
  requiredPermissions,
  userRole
}: AccessDeniedProps): JSX.Element {
  let message = 'You do not have permission to access this page.';

  if (requiredRole) {
    message = `This page requires ${requiredRole} role. Your role: ${userRole}`;
  } else if (requiredRoles) {
    message = `This page requires one of these roles: ${requiredRoles.join(', ')}. Your role: ${userRole}`;
  } else if (requiredPermission) {
    message = `This page requires '${requiredPermission}' permission. Your role (${userRole}) does not have this permission.`;
  } else if (requiredPermissions) {
    message = `This page requires the following permissions: ${requiredPermissions.join(', ')}. Your role (${userRole}) does not have these permissions.`;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-[#050505]">
      <div className="text-center p-8 bg-white dark:bg-[#111] rounded-lg shadow-lg max-w-md border-2 border-red-500/20">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-red-600 dark:text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
          Access Denied
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {message}
        </p>
        <a
          href="/dashboard"
          className="inline-block px-6 py-3 bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 transition-colors"
        >
          Back to Dashboard
        </a>
      </div>
    </div>
  );
}

/**
 * Utility function to check if user can perform action
 */
export function canPerformAction(userRole: string, permission: Permission): boolean {
  const role = userRole as UserRole;
  const permissions = rolePermissions[role] || [];
  return permissions.includes(permission);
}

/**
 * Get readable role name
 */
export function getRoleName(role: UserRole): string {
  const roleNames = {
    [UserRole.ADMIN]: 'Administrator',
    [UserRole.USER]: 'User',
    [UserRole.VIEWER]: 'Viewer',
  };
  return roleNames[role] || role;
}

/**
 * Get role description
 */
export function getRoleDescription(role: UserRole): string {
  const descriptions = {
    [UserRole.ADMIN]: 'Full access to all features and user management',
    [UserRole.USER]: 'Can create and manage projects, tasks, and jobs',
    [UserRole.VIEWER]: 'Read-only access to view projects and data',
  };
  return descriptions[role] || '';
}
